import type { ReactNode } from 'react';

export type NotificationVariant = 'error' | 'success' | 'info' | 'warning';

export interface NotificationProps {
  /** Tone of the message. Drives colour and the default ARIA role. */
  variant: NotificationVariant;
  /** Optional bold lead line above the body. */
  title?: string;
  /** Message body. */
  children?: ReactNode;
  /**
   * Leading status glyph. Defaults to a tone glyph; pass `false` to omit.
   * Icons are decorative (aria-hidden) - tone is also carried by title/text.
   */
  icon?: ReactNode | false;
  /** Optional trailing action slot, e.g. a retry button. */
  action?: ReactNode;
  /** When provided, renders a dismiss button that calls this on activation. */
  onDismiss?: () => void;
  /**
   * Override the derived ARIA role. Default: `error` -> `alert` (assertive);
   * `success`/`info`/`warning` -> `status` (polite).
   */
  role?: 'alert' | 'status';
  className?: string;
  id?: string;
}

const TONE_GLYPH: Record<NotificationVariant, string> = {
  error: '!',
  success: '✓',
  info: 'i',
  warning: '!',
};

export function Notification({
  variant,
  title,
  children,
  icon,
  action,
  onDismiss,
  role,
  className,
  id,
}: NotificationProps) {
  const resolvedRole = role ?? (variant === 'error' ? 'alert' : 'status');
  const classes = ['nuff-notification', `nuff-notification--${variant}`, className]
    .filter(Boolean)
    .join(' ');

  const glyph = icon === false ? null : (icon ?? TONE_GLYPH[variant]);

  return (
    <div
      className={classes}
      role={resolvedRole}
      aria-live={resolvedRole === 'status' ? 'polite' : undefined}
      {...(id ? { id } : {})}
    >
      {glyph !== null && (
        <span className="nuff-notification__icon" aria-hidden="true">
          {glyph}
        </span>
      )}
      <div className="nuff-notification__content">
        {title && <p className="nuff-notification__title">{title}</p>}
        {children !== undefined && children !== null && (
          <div className="nuff-notification__body">{children}</div>
        )}
      </div>
      {action && <div className="nuff-notification__action">{action}</div>}
      {onDismiss && (
        <button
          type="button"
          className="nuff-notification__dismiss"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          {'×'}
        </button>
      )}
    </div>
  );
}
